// src/features/files/components/FileExplorer/modules/InlineRename.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';

// How long a click on a folder's name waits for a second click before opening it
// (the second press cancels the wait, so this only delays a single click)
const DOUBLE_CLICK_MS = 400;

/** Whether an actions list (keys or { key, label }) has `key`. */
export const hasAction = (actions, key) =>
  Boolean(actions?.some(action => (typeof action === 'string' ? action : action.key) === key));

/**
 * Renaming an item where its name is shown: double-click the name, or press F2
 * on the focused row/card. Spread `nameProps` on the element showing the name
 * and `rowKeyDown` into the row's key handler.
 *
 * Folders open on a single click, so pass `onOpen` for them: a click on the name
 * then waits a moment for a possible second click before opening the folder.
 */
export function useInlineRename({ enabled, onOpen }) {
  const [renaming, setRenaming] = useState(false);
  const openTimer = useRef(null);
  useEffect(() => () => clearTimeout(openTimer.current), []);

  const nameProps = enabled ? {
    onMouseDown: onOpen ? (event) => {
      if (event.detail >= 2) clearTimeout(openTimer.current);
    } : undefined,
    onClick: onOpen ? (event) => {
      event.stopPropagation();
      if (event.detail === 1) openTimer.current = setTimeout(onOpen, DOUBLE_CLICK_MS);
    } : undefined,
    onDoubleClick: (event) => {
      event.stopPropagation();
      clearTimeout(openTimer.current);
      setRenaming(true);
    },
  } : {};

  // True when it handled the key
  const rowKeyDown = (event) => {
    if (!enabled || event.key !== 'F2') return false;
    event.preventDefault();
    setRenaming(true);
    return true;
  };

  return { renaming, stopRenaming: () => setRenaming(false), nameProps, rowKeyDown };
}

const stop = (event) => event.stopPropagation();

/**
 * Text field for a new name. Enter or leaving the field saves, Escape cancels.
 * With `keepExtension`, only the part before the extension is edited.
 * `onSave(newName)` resolves true when it worked; until then the field stays,
 * marked as an error if it failed. `onClose({ keyboard })` ends the editing.
 */
export function InlineRenameField({ name, keepExtension = false, onSave, onClose, sx }) {
  const { t } = useTranslation(['files']);
  const dot = keepExtension ? name.lastIndexOf('.') : -1;
  const extension = dot > 0 ? name.slice(dot) : '';
  const [value, setValue] = useState(dot > 0 ? name.slice(0, dot) : name);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // Enter followed by the blur it causes must not save twice
  const done = useRef(false);
  const busy = useRef(false);

  const finish = (keyboard) => {
    done.current = true;
    onClose({ keyboard });
  };

  const save = async (keyboard) => {
    if (done.current || busy.current) return;
    const newName = value.trim() + extension;
    if (!value.trim() || newName === name) {
      finish(keyboard);
      return;
    }
    busy.current = true;
    setSaving(true);
    const ok = await onSave(newName);
    busy.current = false;
    setSaving(false);
    if (ok) finish(keyboard);
    else setFailed(true);
  };

  return (
    <TextField
      value={value}
      onChange={(event) => { setValue(event.target.value); setFailed(false); }}
      autoFocus
      onFocus={(event) => event.target.select()}
      onBlur={() => save(false)}
      // Keys and clicks stay here; the row would open, select or start dragging
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          save(true);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          finish(true);
        }
      }}
      onClick={stop}
      onPointerDown={stop}
      onDoubleClick={stop}
      size="small"
      fullWidth
      error={failed}
      sx={sx}
      slotProps={{
        htmlInput: { readOnly: saving, 'aria-label': t('files:rename_inline', { name }), sx: { py: 0.5, fontSize: 'inherit' } },
        input: {
          endAdornment: (extension || saving) && (
            <InputAdornment position="end" sx={{ ml: 0.25, '& p': { fontSize: 'inherit' } }}>
              {saving ? <CircularProgress size={14} /> : extension}
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
